/**
 * First-touch signup attribution for app.facetflux.com.
 *
 * Reads UTM parameters and ad click IDs off the landing URL — either a direct ad
 * click into the app, or the ones www.facetflux.com forwards on its app links
 * (BrandKarma SDK `withAttribution`) — and keeps the FIRST capture in
 * first-party localStorage until signup, when it is sent with the register
 * request (or through the Google sign-in state) and stored on the user.
 *
 * This is first-party product attribution only: nothing here is sent to an ad
 * platform. Forwarding click IDs to ad platforms (offline / server-side
 * conversions) must be gated on the ads consent snapshot the backend stores
 * alongside them (see docs in the PR / Ops notes).
 */

const STORAGE_KEY = "ff_attribution";
const STORAGE_VERSION = 1;
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const MAX_VALUE_LENGTH = 512; // same cap as the BrandKarma beacon / backend

/** Query parameter → register DTO field. */
export const ATTRIBUTION_PARAMS = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
  utm_id: "utmId",
  gclid: "gclid", // Google Ads
  gbraid: "gbraid", // Google Ads (iOS web-to-app)
  wbraid: "wbraid", // Google Ads (iOS app-to-web)
  fbclid: "fbclid", // Meta
  li_fat_id: "liFatId", // LinkedIn
  msclkid: "msclkid", // Microsoft Ads
  rdt_cid: "rdtCid", // Reddit
  twclid: "twclid", // X
  ttclid: "ttclid", // TikTok
};

// Routes whose path carries a secret (login link / confirmation token): keep the prefix only.
const SENSITIVE_PATHS = [
  /^\/auth\/login\/[^/]+/,
  /^\/auth\/confirm-email\/[^/]+/,
  /^\/invitations?\/[^/]+/,
  /^\/vendor\/invitation\/[^/]+/i,
];

function clip(value) {
  const v = (value ?? "").trim();
  return v ? v.slice(0, MAX_VALUE_LENGTH) : null;
}

function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function redactPath(pathname) {
  for (const re of SENSITIVE_PATHS) {
    const m = pathname.match(re);
    if (m) return m[0].replace(/\/[^/]+$/, "/:token");
  }
  return pathname;
}

/**
 * Build the attribution record from a URL + referrer. Returns null when there is
 * nothing worth recording (no UTM / click id and no external referrer).
 */
export function parseAttribution(href, referrer, now = new Date()) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const params = url.searchParams;
  const record = {};
  const kept = new URLSearchParams();
  for (const [param, field] of Object.entries(ATTRIBUTION_PARAMS)) {
    const v = clip(params.get(param));
    if (v) {
      record[field] = v;
      kept.set(param, v);
    }
  }
  // `?ref=` shorthand → utm_source (same rule as the www SDK).
  const ref = clip(params.get("ref"));
  if (ref && !record.utmSource) {
    record.utmSource = ref;
    kept.set("utm_source", ref);
  }

  let externalReferrer = null;
  if (referrer) {
    try {
      const r = new URL(referrer);
      if (r.host !== url.host && /^https?:$/.test(r.protocol)) {
        externalReferrer = `${r.origin}${r.pathname}`.slice(0, 2048);
      }
    } catch {
      /* ignore malformed referrer */
    }
  }

  if (Object.keys(record).length === 0 && !externalReferrer) return null;

  // Landing page without arbitrary query values (they can carry tokens): path
  // (secret segments redacted) + the attribution parameters only.
  const query = kept.toString();
  record.landingPage = `${url.origin}${redactPath(url.pathname)}${query ? `?${query}` : ""}`.slice(0, 2048);
  if (externalReferrer) record.referrer = externalReferrer;
  record.firstCapturedAt = now.toISOString();
  return record;
}

function readStored(now = Date.now()) {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const captured = Date.parse(parsed?.data?.firstCapturedAt);
    if (parsed?.v !== STORAGE_VERSION || !parsed.data || !(now - captured < TTL_MS)) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

/**
 * Capture attribution from the current URL. First touch wins: an existing,
 * unexpired capture is never overwritten. Call once on app load.
 */
export function captureAttribution(
  href = window.location.href,
  referrer = document.referrer
) {
  const existing = readStored();
  if (existing) return existing;
  const record = parseAttribution(href, referrer);
  if (!record) return null;
  const storage = safeStorage();
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, data: record }));
  } catch {
    /* storage blocked: attribution is lost on reload, acceptable */
  }
  return record;
}

/** The stored first-touch attribution (register DTO shape), or null. */
export function getAttribution() {
  return readStored();
}

/** Drop the stored attribution once it has been sent with a successful signup. */
export function clearAttribution() {
  try {
    safeStorage()?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
